import { DellCloseRingDuotone } from '@repo/go-icons';
import { Button, Scrollbar } from '@repo/go-ui';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useStore } from 'src/store';
import { GOPLAN_COLOR_PALETTE_EDITING } from 'src/store/roles-store/roles';

import type { PaletteSettingsStore } from '../palette-settings.store';

import styles from './directories-list.module.scss';

type Props = {
  store: PaletteSettingsStore;
};

export const DirectoriesList = observer<Props>(function DirectoriesList({ store }) {
  const { t } = useTranslation();
  const { rolesStore } = useStore();

  const { active, archived } = useMemo(() => {
    if (!store.subSection) {
      return { active: [], archived: [] };
    }

    return store.settingsItems[store.subSection] ?? { active: [], archived: [] };
  }, [store.settingsItems, store.subSection]);

  const filteredItems = useMemo(() => {
    return (store.dictsStatus === 'active' ? active : archived).filter((item) =>
      item.name.toLowerCase().includes(store.searchValue.toLowerCase()),
    );
  }, [active, archived, store.dictsStatus, store.searchValue]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.listBlock}>
        <div className={clsx(styles.listItem, styles.headersBlock)}>
          <h3 className={styles.listHeader}>{t('labels:object')}</h3>
          <h3 className={styles.listHeader}>{t('labels:color')}</h3>
        </div>
        <Scrollbar>
          <ul className={styles.list}>
            {filteredItems.map((item) => {
              const isActive = store.currentObject === item;
              const color = item.color;

              return (
                <li key={item.entityId} className={styles.listItem}>
                  <p className={styles.listItemName}>{item.name}</p>
                  <div className={styles.buttonsWrapper}>
                    <Button
                      disabled={!rolesStore.hasAccess([GOPLAN_COLOR_PALETTE_EDITING])}
                      variant="flat"
                      className={clsx(styles.colorButton, isActive && styles.colorButton__active)}
                      onClick={() => store.setCurrentObject(item)}
                    >
                      <div
                        style={{
                          backgroundColor: color ?? 'transparent',
                        }}
                        className={styles.colorButtonInner}
                      />
                    </Button>
                    <Button
                      variant="flat"
                      icon={<DellCloseRingDuotone />}
                      style={{
                        color: 'white',
                      }}
                      disabled={!rolesStore.hasAccess([GOPLAN_COLOR_PALETTE_EDITING])}
                      onClick={() => {
                        if (!store.subSection) {
                          return;
                        }

                        store.setColor(store.subSection, item.entityId, null);
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </Scrollbar>
      </div>
    </div>
  );
});
